import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Tag, Plus, Edit, Trash2, Loader2, BookOpen } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function AdminCategories() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch {
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success('Categoria atualizada');
      } else {
        await api.post('/admin/categories', form);
        toast.success('Categoria criada');
      }
      setDialogOpen(false);
      loadCategories();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!confirm(`Eliminar a categoria "${cat.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      toast.success('Categoria eliminada');
      loadCategories();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao eliminar'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-categories">
      <Navbar />
      <main className="pt-20 md:pt-24 pb-20 md:pb-12 px-4 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
                <Tag className="w-7 h-7 text-ruby" />
                Categorias de Contos
              </h1>
              <p className="text-muted-foreground mt-1">{categories.length} categorias</p>
            </div>
            <Button onClick={openCreate} className="bg-ruby hover:bg-ruby-light" data-testid="create-category-btn">
              <Plus className="w-4 h-4 mr-2" />Nova Categoria
            </Button>
          </div>

          {/* Categories List */}
          <div className="space-y-3">
            {categories.map((cat) => (
              <Card key={cat.id} className="bg-surface border-border" data-testid={`category-row-${cat.slug}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-foreground text-lg">{cat.name}</h3>
                      <Badge variant="outline" className="border-border text-muted-foreground text-xs">{cat.slug}</Badge>
                      <Badge className="bg-ruby/10 text-ruby text-xs flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />{cat.story_count}
                      </Badge>
                    </div>
                    {cat.description && (
                      <p className="text-sm text-muted-foreground truncate">{cat.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(cat)}
                      className="border-border"
                      data-testid={`edit-category-${cat.slug}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(cat)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-category-${cat.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {categories.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Tag className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nenhuma categoria criada</p>
                <Button onClick={openCreate} variant="outline" className="mt-4 border-ruby text-ruby">
                  <Plus className="w-4 h-4 mr-2" />Criar primeira categoria
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editing ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Romance, Suspense..."
                className="bg-background border-border"
                data-testid="category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Breve descrição da categoria..."
                className="bg-background border-border"
                rows={3}
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-ruby hover:bg-ruby-light" data-testid="save-category-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? 'Guardar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
